import {
  type AssignSiteToStudyRequest,
  type AssignStudyToSiteRequest,
  type IStudySiteAssignmentRepository,
} from "../repositories/StudySiteAssignmentRepository";
import type {
  StudySiteAssignment,
  CreateStudySiteAssignmentRequest,
  UpdateStudySiteAssignmentRequest,
  StudySiteAssignmentFilters,
  StudySiteAssignmentListResponse,
  StudySiteAssignmentStatistics,
} from "../pages/StudyManagement/types/studySiteAssignment.types";
import {
  STUDY_SITE_ASSIGNMENT_STATUS,
  isStudySiteAssignmentStatus,
  isAnyStudySiteAssignmentStatus,
} from "@/types";
import { getErrorMessage, isApiError } from "../utils/errorMessage";
export interface IStudySiteAssignmentUseCase {
  // Assignment management
  createAssignment(
    assignment: CreateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment>;
  getAssignments(
    studyId: string,
    filters?: StudySiteAssignmentFilters,
    page?: number,
    limit?: number,
  ): Promise<StudySiteAssignmentListResponse>;
  getAssignmentsByStudy(studyId: string): Promise<StudySiteAssignment[]>;
  getAssignmentsBySite(siteId: string): Promise<StudySiteAssignment[]>;
  getAssignmentById(id: string): Promise<StudySiteAssignment>;
  updateAssignment(
    id: string,
    updates: UpdateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment>;
  deleteAssignment(id: string): Promise<void>;
  // Status management
  activateAssignment(id: string): Promise<StudySiteAssignment>;
  deactivateAssignment(id: string): Promise<StudySiteAssignment>;
  completeAssignment(id: string): Promise<StudySiteAssignment>;
  // Bulk operations
  assignSitesToStudy(
    studyId: string,
    siteAssignments: AssignSiteToStudyRequest[],
  ): Promise<StudySiteAssignment[]>;
  assignStudiesToSite(
    siteId: string,
    studyAssignments: (string | AssignStudyToSiteRequest)[],
  ): Promise<StudySiteAssignment[]>;
  removeSiteFromStudy(studyId: string, siteId: string): Promise<void>;
  removeStudyFromSite(siteId: string, studyId: string): Promise<void>;
  // Statistics
  getAssignmentStatistics(
    studyId: string,
  ): Promise<StudySiteAssignmentStatistics>;
}
export class StudySiteAssignmentUseCase implements IStudySiteAssignmentUseCase {
  constructor(
    private studySiteAssignmentRepository: IStudySiteAssignmentRepository,
  ) {}
  async createAssignment(
    assignment: CreateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment> {
    // Validate required fields
    if (!assignment.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!assignment.siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!assignment.principalInvestigator?.trim()) {
      throw new Error("Site Contact is required");
    }
    if (!Array.isArray(assignment.coordinators)) {
      throw new Error("Coordinators must be an array");
    }
    if (
      typeof assignment.targetEnrollment !== "number" ||
      assignment.targetEnrollment <= 0
    ) {
      throw new Error("Target enrollment must be a positive number");
    }
    try {
      return await this.studySiteAssignmentRepository.createAssignment(
        assignment,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to create study site assignment",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid study site assignment data provided"),
        );
      }
      throw error;
    }
  }
  async getAssignments(
    studyId: string,
    filters?: StudySiteAssignmentFilters,
    page = 1,
    limit = 10,
  ): Promise<StudySiteAssignmentListResponse> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.studySiteAssignmentRepository.getAssignments(
        studyId,
        filters,
        page,
        limit,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to view study site assignments",
        );
      }
      throw error;
    }
  }
  async getAssignmentsByStudy(studyId: string): Promise<StudySiteAssignment[]> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.studySiteAssignmentRepository.getAssignmentsByStudy(
        studyId,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to view study site assignments",
        );
      }
      throw error;
    }
  }
  async getAssignmentsBySite(siteId: string): Promise<StudySiteAssignment[]> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    try {
      return await this.studySiteAssignmentRepository.getAssignmentsBySite(
        siteId,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to view study site assignments",
        );
      }
      throw error;
    }
  }
  async getAssignmentById(id: string): Promise<StudySiteAssignment> {
    if (!id?.trim()) {
      throw new Error("Assignment ID is required");
    }
    try {
      return await this.studySiteAssignmentRepository.getAssignmentById(id);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to view study site assignment",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study site assignment not found");
      }
      throw error;
    }
  }
  async updateAssignment(
    id: string,
    updates: UpdateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment> {
    if (!id?.trim()) {
      throw new Error("Assignment ID is required");
    }
    // Validate coordinators if provided
    if (
      updates.coordinators !== undefined &&
      !Array.isArray(updates.coordinators)
    ) {
      throw new Error("Coordinators must be an array");
    }
    // Validate target enrollment if provided
    if (
      updates.targetEnrollment !== undefined &&
      (typeof updates.targetEnrollment !== "number" ||
        updates.targetEnrollment <= 0)
    ) {
      throw new Error("Target enrollment must be a positive number");
    }
    // Validate current enrollment if provided
    if (
      updates.currentEnrollment !== undefined &&
      (typeof updates.currentEnrollment !== "number" ||
        updates.currentEnrollment < 0)
    ) {
      throw new Error("Current enrollment must be a non-negative number");
    }
    try {
      // Get current assignment to validate update
      const currentAssignment =
        await this.studySiteAssignmentRepository.getAssignmentById(id);
      // Validate current enrollment against target
      if (
        updates.currentEnrollment !== undefined &&
        updates.currentEnrollment >
          (updates.targetEnrollment ?? currentAssignment.targetEnrollment)
      ) {
        throw new Error("Current enrollment cannot exceed target enrollment");
      }
      return await this.studySiteAssignmentRepository.updateAssignment(
        id,
        updates,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to update study site assignment",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study site assignment not found");
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(getErrorMessage(error, "Invalid update data provided"));
      }
      throw error;
    }
  }
  async deleteAssignment(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Assignment ID is required");
    }
    try {
      // Check if assignment exists and get its details
      const assignment =
        await this.studySiteAssignmentRepository.getAssignmentById(id);
      // Only allow deletion of inactive or pending assignments
      if (
        !isAnyStudySiteAssignmentStatus(
          assignment.status,
          STUDY_SITE_ASSIGNMENT_STATUS.INACTIVE,
          STUDY_SITE_ASSIGNMENT_STATUS.PENDING,
        )
      ) {
        throw new Error("Only inactive or pending assignments can be deleted");
      }
      await this.studySiteAssignmentRepository.deleteAssignment(id);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to delete study site assignment",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study site assignment not found");
      }
      throw error;
    }
  }
  async activateAssignment(id: string): Promise<StudySiteAssignment> {
    if (!id?.trim()) {
      throw new Error("Assignment ID is required");
    }
    try {
      // Check if assignment exists and get its details
      const assignment =
        await this.studySiteAssignmentRepository.getAssignmentById(id);
      // Only allow activation of pending assignments
      if (
        !isStudySiteAssignmentStatus(
          assignment.status,
          STUDY_SITE_ASSIGNMENT_STATUS.PENDING,
        )
      ) {
        throw new Error("Only pending assignments can be activated");
      }
      return await this.studySiteAssignmentRepository.activateAssignment(id);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to activate study site assignment",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study site assignment not found");
      }
      throw error;
    }
  }
  async deactivateAssignment(id: string): Promise<StudySiteAssignment> {
    if (!id?.trim()) {
      throw new Error("Assignment ID is required");
    }
    try {
      // Check if assignment exists and get its details
      const assignment =
        await this.studySiteAssignmentRepository.getAssignmentById(id);
      // Only allow deactivation of active assignments
      if (
        !isStudySiteAssignmentStatus(
          assignment.status,
          STUDY_SITE_ASSIGNMENT_STATUS.ACTIVE,
        )
      ) {
        throw new Error("Only active assignments can be deactivated");
      }
      return await this.studySiteAssignmentRepository.deactivateAssignment(id);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to deactivate study site assignment",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study site assignment not found");
      }
      throw error;
    }
  }
  async completeAssignment(id: string): Promise<StudySiteAssignment> {
    if (!id?.trim()) {
      throw new Error("Assignment ID is required");
    }
    try {
      // Check if assignment exists and get its details
      const assignment =
        await this.studySiteAssignmentRepository.getAssignmentById(id);
      // Only allow completion of active assignments
      if (
        !isStudySiteAssignmentStatus(
          assignment.status,
          STUDY_SITE_ASSIGNMENT_STATUS.ACTIVE,
        )
      ) {
        throw new Error("Only active assignments can be completed");
      }
      return await this.studySiteAssignmentRepository.completeAssignment(id);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to complete study site assignment",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study site assignment not found");
      }
      throw error;
    }
  }
  async getAssignmentStatistics(
    studyId: string,
  ): Promise<StudySiteAssignmentStatistics> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.studySiteAssignmentRepository.getAssignmentStatistics(
        studyId,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to view statistics",
        );
      }
      throw error;
    }
  }
  async assignSitesToStudy(
    studyId: string,
    siteAssignments: AssignSiteToStudyRequest[],
  ): Promise<StudySiteAssignment[]> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!Array.isArray(siteAssignments) || siteAssignments.length === 0) {
      throw new Error("At least one site assignment is required");
    }
    if (
      !siteAssignments.every(
        (assignment) =>
          typeof assignment.siteId === "string" && assignment.siteId.trim(),
      )
    ) {
      throw new Error(
        "All site assignment entries must contain a valid siteId",
      );
    }
    try {
      return await this.studySiteAssignmentRepository.assignSitesToStudy(
        studyId,
        siteAssignments,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to assign sites to study",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid site or study data provided"),
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study or one or more sites not found");
      }
      throw error;
    }
  }
  async assignStudiesToSite(
    siteId: string,
    studyAssignments: (string | AssignStudyToSiteRequest)[],
  ): Promise<StudySiteAssignment[]> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!Array.isArray(studyAssignments) || studyAssignments.length === 0) {
      throw new Error("At least one study ID is required");
    }
    if (
      !studyAssignments.every(
        (assignment) =>
          (typeof assignment === "string" && assignment.trim()) ||
          (typeof assignment === "object" &&
            typeof assignment.studyId === "string" &&
            assignment.studyId.trim()),
      )
    ) {
      throw new Error("All study assignments must contain a valid study ID");
    }
    try {
      return await this.studySiteAssignmentRepository.assignStudiesToSite(
        siteId,
        studyAssignments,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to assign studies to site",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid study or site data provided"),
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Site or one or more studies not found");
      }
      throw error;
    }
  }
  async removeSiteFromStudy(studyId: string, siteId: string): Promise<void> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    try {
      await this.studySiteAssignmentRepository.removeSiteFromStudy(
        studyId,
        siteId,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to remove site from study",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Study or site assignment not found");
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Failed to remove site from study"),
        );
      }
      throw error;
    }
  }
  async removeStudyFromSite(siteId: string, studyId: string): Promise<void> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      await this.studySiteAssignmentRepository.removeStudyFromSite(
        siteId,
        studyId,
      );
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to remove study from site",
        );
      }
      if (isApiError(error) && error.status === 404) {
        throw new Error("Site or study assignment not found");
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Failed to remove study from site"),
        );
      }
      throw error;
    }
  }
}
